import { HELP_CENTER_ARTICLES } from '@lib/customer-support.const';

import { IIncabAlertModes, IIncabAlertsLanguageLocalization, IVehiclesDpeScalesAndExponents } from './company.interface';

const SECS_PER_MIN: number = 60;
export const POSTED_SPEED_DURATIONS: number[] = [0.5, 1, 2, 3, 5, 10, 15, 20].map(v => v * SECS_PER_MIN);
export const OVER_POSTED_SPEED_DURATIONS: number[] = [2, 3, 5, 10].map(v => v * SECS_PER_MIN);
export const SPEED_ALERT_SUPPRESS_DURATIONS: number[] = [5, 10, 20, 30, 60].map(v => v * SECS_PER_MIN);
export const DEFAULT_INCAB_SPEEDING_THRESHOLD: number = 55;
export const DEFAULT_INCAB_SPEEDING_THRESHOLD_IN_MPH: number = 34;
export const DEFAULT_INCAB_SPEEDING_OVER_POSTED_DURATION: number = 60;
export const DEFAULT_SPEEDING_THRESHOLD_IN_MPH: number = 6;
export const REGEX_FLOATS = /^[0-9]+(\.[0-9]+)?$/;
export const SMART_THRESHOLD_UNAVAILABLE: number = -1;
export const EMPTY_STOP_NAME_ERROR: string = $localize`Custom stop names cannot be empty`;
export const NO_STOP_TYPE_ERROR: string = $localize`At least one stop type is required`;
export const INCAB_SPEEDING_OVER_POSTED_DURATION_MIN: number = 5;
export const INCAB_SPEEDING_OVER_POSTED_DURATION_MAX: number = 1200;
export const CAMERA_AUDIO_MESSAGE: string =
  $localize`This feature enables audio recording on the Motive Smart Dashcam.
  Certain states and other jurisdictions have rules governing the collection and use of audio and video recordings.
  We will only enable this feature at your direction. By enabling this feature and clicking confirm,
  you acknowledge and agree that you have obtained all legally required consents to activate audio recordings.
  Once enabled, you can turn this feature off at any time through the dashboard or by contacting support@gomotive.com.`
;
export const LIVE_STREAM_MESSAGE: string =
  $localize`This feature allows you to enable live streaming from the Motive AI Dashcam.
  Before enabling, Motive recommends that you review all applicable laws and internal policies that may govern the
  use of the feature. By enabling live streaming, you acknowledge and agree that you have obtained all legally required
  consents to activate the feature and will comply with any applicable laws governing its use. Once enabled, you can turn
  this feature off at any time through the dashboard or by contacting support@gomotive.com.`
;
const privacyArticleLinkTxt = $localize`Learn more`;
export const PRIVACY_ARTICLE_LINK: string = `<a href="${HELP_CENTER_ARTICLES.MOTIVE_PRIVACY_CONSIDERATION}" target="_blank"> ${privacyArticleLinkTxt} </a>`;
export const OVERRIDE_MESSAGE: string =
  $localize`Remember that you can choose to override this setting for specific vehicles from the Edit Vehicle page.`
;
export const ENGINE_OFF_SECONDARY_MESSAGE: string =
  $localize`Even with default recording settings in place, Engine-Off-Recording <strong>could deplete a vehicle\'s battery</strong> to the point where the vehicle won\'t start.`
;

export enum CameraSettings {
  On = 'on',
  Off = 'off',
  OnIfEngineOn = 'on_if_engine_on',
  OffWhileIdling = 'off_while_idling',
  NoOverride = 'no_override',
}

export enum AI_DRIVER_IDENTIFICATION_SETTINGS {
  NONE = 'none',
  TELEMATICS = 'telematics',
  FACIAL_RECOGNITION = 'face_match',
  TELEMATICS_FACIAL = 'telematics_and_face_match',
}

export const INCAB_AUDIO_RECORDING_LABELS = {
  [CameraSettings.On]: $localize`On`,
  [CameraSettings.Off]: $localize`Off`,
};

export const ENGINE_OFF_RECORDING_LABELS = {
  [CameraSettings.On]: $localize`On`,
  [CameraSettings.Off]: $localize`Off`,
};

export const ROAD_ENGINE_OFF_RECORDING_LABELS = {
  [CameraSettings.On]: $localize`Enabled`,
  [CameraSettings.Off]: $localize`Disabled`,
};

export const DRIVER_FACING_CAMERA_LABELS: Partial<Record<CameraSettings, string>> = {
  [CameraSettings.On]: $localize`Always on`,
  [CameraSettings.Off]: $localize`Always off`,
  [CameraSettings.OnIfEngineOn]: $localize`Only record when the engine is on`,
  [CameraSettings.OffWhileIdling]: $localize`Off when vehicle is stationary or idling`,
};

// Changed name from 'Telematics Data' to 'Driver App Data'. The param would remain as 'telematics_data' until further changes
export const AI_IDENTIFICATION_LABELS = {
  [AI_DRIVER_IDENTIFICATION_SETTINGS.NONE]: $localize`None (disabled)`,
  [AI_DRIVER_IDENTIFICATION_SETTINGS.TELEMATICS]: $localize`Driver App Data`,
  [AI_DRIVER_IDENTIFICATION_SETTINGS.FACIAL_RECOGNITION]: $localize`Face Match (Facial Recognition)`,
  [AI_DRIVER_IDENTIFICATION_SETTINGS.TELEMATICS_FACIAL]: $localize`Driver App Data and Face Match`,
};

export const ROAD_IMAGE_BREADCRUMB_LABELS = {
  [CameraSettings.On]: $localize`On`,
  [CameraSettings.Off]: $localize`Off`,
};

export const IMAGE_BREADCRUMB_LABELS = {
  [CameraSettings.On]: $localize`Every hour`,
  [CameraSettings.Off]: $localize`Off`,
  [CameraSettings.OffWhileIdling]: $localize`On (images are captured when vehicle is in motion)`,
};

export const LIVE_STREAM_LABELS = {
  [CameraSettings.On]: $localize`On`,
  [CameraSettings.Off]: $localize`Off`,
  [CameraSettings.OffWhileIdling]: $localize`On (only when vehicle is in motion)`,
};

export const DRIVER_MATCH_LABELS = {
  [CameraSettings.On]: $localize`On`,
  [CameraSettings.Off]: $localize`Off`,
};

export const DRIVER_INCAB_ALERTS = {
  incabAlertCellPhoneUsageMode: 'incabAlertCellPhoneUsageMode',
  incabAlertSeatBeltMode: 'incabAlertSeatBeltMode',
};

export const HUBBLE_INCAB_ALERTS = {
  incabAlertCloseFollowingMode: 'incabAlertCloseFollowingMode',
  ...DRIVER_INCAB_ALERTS,
};

export const INCAB_ALERTS: Record<keyof IIncabAlertModes, string> = {
  incabAlertUdeMode: 'incabAlertUdeMode',
  incabAlertDpeMode: 'incabAlertDpeMode',
  incabAlertUdeModeOverridable: 'incabAlertUdeModeOverridable',
  incabAlertSpeedingMode: 'incabAlertSpeedingMode',
  incabAlertRollingStopMode: 'incabAlertRollingStopMode',
  incabAlertDistractionMode: 'incabAlertDistractionMode',
  incabAlertDrowsinessMode: 'incabAlertDrowsinessMode',
  incabAlertForwardCollisionWarningMode: 'incabAlertForwardCollisionWarningMode',
  incabAlertUnsafeLaneChangeMode: 'incabAlertUnsafeLaneChangeMode',
  incabAlertCameraObstructionMode: 'incabAlertCameraObstructionMode',
  incabAlertSpeedingOverPostedMode: 'incabAlertSpeedingOverPostedMode',
  ...HUBBLE_INCAB_ALERTS,
};

export const INCAB_ALERTS_LANGUAGE_LOCALIZATION_MODES: IIncabAlertsLanguageLocalization[] = [
  {
    value: 1,
    label: 'English',
  },
  {
    value: 2,
    label: 'French',
  },
  {
    value: 3,
    label: 'Spanish',
  },
];

export const INCAB_ALERTS_SECONDARY_LANGUAGE_LOCALIZATION_MODES: IIncabAlertsLanguageLocalization[] = [
  ...INCAB_ALERTS_LANGUAGE_LOCALIZATION_MODES,
  {
    value: 4,
    label: 'None',
  },
];

export type IncabAlert = keyof typeof INCAB_ALERTS;

export const SAFETY_DPE_SCALES_AND_EXPONENTS: IVehiclesDpeScalesAndExponents = {
  lightDuty: {
    HardAccel: {
      dpeScale: 1.675895182e-05,
      dpeExponent: 8.63861082,
    },
    HardBrake: {
      dpeScale: 4.726668963e-05,
      dpeExponent: 7.824081828,
    },
    HardCorner: {
      dpeScale: 5.184525554e-05,
      dpeExponent: 10.197887588,
    },
  },
  mediumDuty: {
    HardAccel: {
      dpeScale: 2.362430428e-05,
      dpeExponent: 8.368867001,
    },
    HardBrake: {
      dpeScale: 3.238358286e-05,
      dpeExponent: 8.121142363,
    },
    HardCorner: {
      dpeScale: 8.155211311e-05,
      dpeExponent: 9.729664944,
    },
  },
  heavyDuty: {
    HardAccel: {
      dpeScale: 1.996673816e-07,
      dpeExponent: 12.118602026,
    },
    HardBrake: {
      dpeScale: 1.274705157e-05,
      dpeExponent: 8.853558431,
    },
    HardCorner: {
      dpeScale: 0.0005114763438,
      dpeExponent: 7.831926137,
    },
  },
};

export enum CameraStorageMode {
  HighestVideoStorage = 5,
  HighVideoStorage = 4,
  HighestVideoQuality = 3,
  HighVideoQuality = 1,
}

export enum VG5CameraStorageMode {
  HighVideoQuality = 0,
  HighestVideoQuality = 1,
  HighestVideoStorage = 2,
}

export enum DATA_RETENTION_TYPE {
  SAFETY = 'SAFETY',
  COMPLIANCE = 'COMPLIANCE',
  MESSAGING = 'MESSAGING',
  LOCATION = 'LOCATION',
}

export enum DATA_RENTENTION_DURATION {
  DEFAULT = 'DEFAULT',
  ONE_YEAR = '1Y',
  SIX_MONTHS = '6M',
  ONE_MONTH = '1M',
  TWO_WEEKS = '2W',
}

export const DATA_RETENTION_DURATION_OPTION_LABEL: Record<DATA_RENTENTION_DURATION, string> = {
  [DATA_RENTENTION_DURATION.DEFAULT]: $localize`Store data indefinitely (recommended)`,
  [DATA_RENTENTION_DURATION.ONE_YEAR]: $localize`Store data for 1 year`,
  [DATA_RENTENTION_DURATION.SIX_MONTHS]: $localize`Store data for 6 months`,
  [DATA_RENTENTION_DURATION.ONE_MONTH]: $localize`Store data for 1 month`,
  [DATA_RENTENTION_DURATION.TWO_WEEKS]: $localize`Store data for 2 weeks`,
};

export const ARTICLES = {
  PERIODIC_TRIP_IMAGES: HELP_CENTER_ARTICLES.PERIODIC_TRIP_IMAGES_ARTICLE,
  DRIVER_MATCH: HELP_CENTER_ARTICLES.DRIVER_MATCH_ARTICLE,
  SPEED_SETTINGS_FOR_EACH_VEHICLE: HELP_CENTER_ARTICLES.HOW_TO_SETUP_SPEEDING_SETTINGS_FOR_EACH_VEHICLE,
  AUTOMATED_COACHING: HELP_CENTER_ARTICLES.SAFETY_AUTOMATED_COACHING_ARTICLE,
  SELF_SERVICE_DATA_RETENTION_OVERVIEW_ARTICLE: HELP_CENTER_ARTICLES.SELF_SERVICE_DATA_RETENTION_OVERVIEW_ARTICLE,
  EVENT_CUSTOMIZATION_DRIVE_SCORE_IMPACT: HELP_CENTER_ARTICLES.EVENT_CUSTOMIZATION_DRIVE_SCORE_IMPACT,
  HOW_TO_ENABLE_DRIVER_IDENTIFICATION_SETTINGS: HELP_CENTER_ARTICLES.HOW_TO_ENABLE_DRIVER_IDENTIFICATION_SETTINGS,
  COLLISION_FIRST_RESPONDER: HELP_CENTER_ARTICLES.COLLISION_FIRST_RESPONDER,
  CAMERA_CONFIGURABLE_STORAGE: HELP_CENTER_ARTICLES.CAMERA_CONFIGURABLE_STORAGE,
  DRIVER_SELF_COACHING: HELP_CENTER_ARTICLES.DRIVER_SELF_COACHING,
};
